import { useEffect, useRef, useState } from "react";

import type { WaterBottle, WaterKind } from "../domain/types";
import { SIP_MAX_ML, SIP_MIN_ML, bottleParts, clampSip, mlText } from "../domain/water";
import { ActionBar } from "./ActionBar";
import { IconButton } from "./Button";
import { Chip } from "./Chip";
import { Field } from "./Fields";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import "./Water.css";

/* The water sheets (kit §37, board WaterAdd). Each renders only while open, like every Sheet. */

/** The drink-kind chips: pressable, one on, every kind counting at its volume. */
export function WaterKindChips({
  kinds,
  value,
  onChange,
  labelId,
}: {
  kinds: { value: WaterKind; label: string }[];
  value: WaterKind;
  onChange: (k: WaterKind) => void;
  labelId: string;
}) {
  return (
    <div className="k-water-kinds">
      <p id={labelId} className="k-water-label">
        What it was · counts in full
      </p>
      <div className="k-water-chips" role="group" aria-labelledby={labelId}>
        {kinds.map((k) => (
          <Chip key={k.value} selected={k.value === value} onClick={() => onChange(k.value)}>
            {k.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}

const parseMl = (v: string): number | null => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n > 0 && n <= 5000 ? n : null;
};

/**
 * A bottle by the quarter, or a custom amount (board WaterAdd). With `bottle` the quarters lead
 * and Half is chosen; without one it is Custom and the ml field takes the focus. A typed amount
 * wins over the quarter, so the two never disagree about what Add will log.
 */
export function WaterAddSheet({
  bottle,
  kinds,
  kind: kind0,
  dayLabel,
  onAdd,
  onClose,
}: {
  bottle: WaterBottle | null;
  kinds: { value: WaterKind; label: string }[];
  kind: WaterKind;
  /** "Wed 23 Sep" */
  dayLabel: string;
  onAdd: (a: { ml: number; kind: WaterKind; note?: string }) => void;
  onClose: () => void;
}) {
  const parts = bottle ? bottleParts(bottle) : [];
  const [frac, setFrac] = useState(0.5);
  const [custom, setCustom] = useState("");
  const [kind, setKind] = useState<WaterKind>(kind0);
  const body = useRef<HTMLDivElement>(null);

  // The Sheet focuses its pane on open, after any autofocus below it, so the field takes the
  // focus a frame later.
  useEffect(() => {
    if (bottle) return;
    const id = requestAnimationFrame(() => body.current?.querySelector<HTMLInputElement>("input")?.focus());
    return () => cancelAnimationFrame(id);
  }, [bottle]);

  const typed = parseMl(custom);
  const part = parts.find((p) => p.frac === frac);
  const amount = custom.trim() ? typed : (part?.ml ?? null);
  const note = custom.trim() || !bottle || !part ? undefined : part.frac === 1 ? bottle.name : `${part.label} of ${bottle.name}`;

  const now = new Date();
  const at = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  return (
    <Sheet
      title={bottle ? bottle.name : "Custom amount"}
      sub={bottle ? `Your bottle · ${mlText(bottle.ml)} · ${dayLabel}` : dayLabel}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{
            label: amount ? `Add ${mlText(amount)}` : "Add",
            disabled: !amount,
            reason: custom.trim() ? "Type an amount up to 5,000 ml" : "Choose how much",
            onClick: () => {
              if (!amount) return;
              onAdd({ ml: amount, kind, note });
            },
          }}
        />
      }
    >
      <div className="k-water-sheet" ref={body}>
        {bottle ? (
          <div className="k-water-kinds">
            <p className="k-water-label">How much of it</p>
            <div className="k-water-parts">
              <Segmented<number>
                ariaLabel="How much of it"
                value={custom.trim() ? -1 : frac}
                onChange={(v) => {
                  setFrac(v);
                  setCustom("");
                }}
                options={parts.map((p) => ({
                  value: p.frac,
                  label: (
                    <>
                      <span className="k-water-parts__frac" aria-hidden="true">
                        {p.label}
                      </span>
                      <span className="k-water-parts__ml" aria-hidden="true">
                        {mlText(p.ml)}
                      </span>
                      <span className="k-water-vh">{`${p.word}, ${mlText(p.ml)}`}</span>
                    </>
                  ),
                }))}
              />
            </div>
          </div>
        ) : null}
        <WaterKindChips kinds={kinds} value={kind} onChange={setKind} labelId="water-add-kind" />
        <Field
          label="Custom ml"
          type="number"
          value={custom}
          onChange={setCustom}
          unit="ml"
          min="1"
          max="5000"
          placeholder={bottle ? "Or type an amount" : "How much"}
          invalid={!!custom.trim() && !typed}
          hint={`Logged at ${at} · undo for five seconds after`}
        />
      </div>
    </Sheet>
  );
}

/**
 * The bottles you fill and the size of a sip (the Water board's "Bottles and sip size" row).
 * Bottles are rows with their name and size; a sip is 30–100 ml in tens.
 */
export function WaterBottlesSheet({
  bottles: bottles0,
  sip: sip0,
  onSave,
  onClose,
}: {
  bottles: WaterBottle[];
  sip: number;
  onSave: (v: { bottles: WaterBottle[]; sip: number }) => void;
  onClose: () => void;
}) {
  const [bottles, setBottles] = useState(bottles0);
  const [sip, setSip] = useState(sip0);
  const [name, setName] = useState("");
  const [size, setSize] = useState("");
  const sizeMl = parseMl(size);
  const canAdd = !!name.trim() && sizeMl !== null && bottles.length < 6;

  const add = () => {
    if (!canAdd || sizeMl === null) return;
    const id = `b:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    setBottles([...bottles, { id, name: name.trim(), ml: sizeMl }]);
    setName("");
    setSize("");
  };

  return (
    <Sheet
      title="Bottles and sip size"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", onClick: () => onSave({ bottles, sip }) }}
        />
      }
    >
      <div className="k-water-sheet">
        <RowGroup heading="Your bottles">
          {bottles.length ? (
            bottles.map((b) => (
              <NavRow
                key={b.id}
                kind="action"
                icon={I.bottle}
                title={b.name}
                subtitle={mlText(b.ml)}
                action={
                  <IconButton
                    icon={I.close}
                    label={`Remove ${b.name}`}
                    onClick={() => setBottles(bottles.filter((x) => x.id !== b.id))}
                  />
                }
              />
            ))
          ) : (
            <NavRow kind="value" title="None yet" subtitle="A bottle you fill, logged whole or by the quarter" />
          )}
        </RowGroup>
        <div className="k-water-newbottle">
          <Field label="Add a bottle" type="text" value={name} onChange={setName} placeholder="Steel 1 L" />
          <Field label="Size" type="number" value={size} onChange={setSize} unit="ml" min="50" max="5000" />
          <IconButton icon={I.plus} label="Add this bottle" onClick={add} />
        </div>
        <div className="k-water-kinds">
          <p className="k-water-label">A sip</p>
          <div className="k-water-stepper">
            <IconButton
              icon={I.minus}
              label="Smaller sip"
              onClick={() => setSip(clampSip(sip - 10))}
            />
            <span className="k-water-stepper__v" aria-live="polite">
              {mlText(sip)}
            </span>
            <IconButton icon={I.plus} label="Bigger sip" onClick={() => setSip(clampSip(sip + 10))} />
          </div>
          <p className="k-water-note">
            One tap of Sip logs this much — {SIP_MIN_ML} to {SIP_MAX_ML} ml, for sipping from a bottle at a desk.
          </p>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * A doctor's fluid limit. It replaces the target and becomes a ceiling: nothing is added to it,
 * and nothing on screen or in a reminder asks for more.
 */
export function WaterLimitSheet({
  limitMl,
  onSave,
  onClose,
}: {
  limitMl: number | undefined;
  onSave: (ml: number | undefined) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState(limitMl ? String(limitMl) : "");
  const n = parseMl(v);
  return (
    <Sheet
      title="A doctor's fluid limit"
      sub="For heart or kidney conditions, where a doctor sets how much to drink"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={limitMl ? { label: "Remove the limit", onClick: () => onSave(undefined) } : { label: "Cancel", onClick: onClose }}
          primary={{
            label: "Save",
            disabled: !n,
            reason: "Type the limit in ml",
            onClick: () => n && onSave(n),
          }}
        />
      }
    >
      <Field
        label="Most to drink in a day"
        type="number"
        value={v}
        onChange={setV}
        unit="ml"
        min="500"
        max="5000"
        invalid={!!v.trim() && !n}
        hint="Kaya shows it as a limit, adds nothing for walks or heat, and its reminders only say what you have logged."
      />
    </Sheet>
  );
}
